import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { cn } from '../../../lib/cn';
import {
  COMPANY_COLORS,
  companySchema,
  type CompanyColor,
  type CompanyInput,
  type CompanyRow,
} from './companies.schema';
import styles from './companies.module.css';

const COLOR_LABELS: Record<CompanyColor, string> = {
  yellow: 'צהוב',
  coral: 'קורל',
  olive: 'זית',
  teal: 'טורקיז',
  lilac: 'לילך',
  rose: 'ורוד',
};

const DEFAULT_VALUES: CompanyInput = {
  name: '',
  initials: '',
  color: 'yellow',
  industry: '',
  size: '',
  website: '',
  address: '',
  notes: '',
};

function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 4)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
}

function valuesFromCompany(company: CompanyRow | null): CompanyInput {
  if (!company) return DEFAULT_VALUES;
  return {
    name: company.name,
    initials: company.initials ?? '',
    color: COMPANY_COLORS.includes(company.color as CompanyColor) ? (company.color as CompanyColor) : 'yellow',
    industry: company.industry ?? '',
    size: company.size ?? '',
    website: company.website ?? '',
    address: company.address ?? '',
    notes: company.notes ?? '',
  };
}

interface CompanyFormProps {
  company: CompanyRow | null;
  formId: string;
  disabled?: boolean;
  onSubmit: (values: CompanyInput) => void | Promise<void>;
}

export function CompanyForm({ company, formId, disabled = false, onSubmit }: CompanyFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CompanyInput>({
    resolver: zodResolver(companySchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    reset(valuesFromCompany(company));
  }, [company, reset]);

  const nameField = register('name');

  return (
    <form id={formId} className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className={styles.field}>
        <label htmlFor="company-name">שם חברה</label>
        <input
          id="company-name"
          type="text"
          className={cn(styles.input, errors.name && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...nameField}
          onBlur={(event) => {
            void nameField.onBlur(event);
            if (!getValues('initials')?.trim()) {
              setValue('initials', getInitials(event.target.value), { shouldDirty: true, shouldValidate: true });
            }
          }}
        />
        {errors.name ? <p className={styles.error}>{errors.name.message}</p> : null}
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="company-initials">ראשי תיבות</label>
          <input
            id="company-initials"
            type="text"
            maxLength={4}
            className={cn(styles.input, errors.initials && styles.invalid)}
            disabled={disabled || isSubmitting}
            {...register('initials')}
          />
          {errors.initials ? <p className={styles.error}>{errors.initials.message}</p> : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="company-color">צבע</label>
          <select
            id="company-color"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('color')}
          >
            {COMPANY_COLORS.map((color) => (
              <option key={color} value={color}>
                {COLOR_LABELS[color]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.palette} aria-label="צבעי חברה">
        {COMPANY_COLORS.map((color) => (
          <span key={color} className={cn(styles.swatch, styles[color])} title={COLOR_LABELS[color]} />
        ))}
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="company-industry">תעשייה</label>
          <input
            id="company-industry"
            type="text"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('industry')}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="company-size">גודל</label>
          <input
            id="company-size"
            type="text"
            className={styles.input}
            disabled={disabled || isSubmitting}
            {...register('size')}
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="company-website">אתר</label>
        <input
          id="company-website"
          type="url"
          dir="ltr"
          className={cn(styles.input, errors.website && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('website')}
        />
        {errors.website ? <p className={styles.error}>{errors.website.message}</p> : null}
      </div>

      <div className={styles.field}>
        <label htmlFor="company-address">כתובת</label>
        <input
          id="company-address"
          type="text"
          className={styles.input}
          disabled={disabled || isSubmitting}
          {...register('address')}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor="company-notes">הערות</label>
        <textarea
          id="company-notes"
          rows={5}
          className={cn(styles.input, styles.textarea, errors.notes && styles.invalid)}
          disabled={disabled || isSubmitting}
          {...register('notes')}
        />
        {errors.notes ? <p className={styles.error}>{errors.notes.message}</p> : null}
      </div>
    </form>
  );
}
